import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/UI';

// ── Replace this with your actual Venmo username ──────────────────────────────
const VENMO_USERNAME = process.env.REACT_APP_VENMO_USERNAME || 'Your-Venmo-Username-Here';
// ─────────────────────────────────────────────────────────────────────────────

const SUGGESTED_AMOUNTS = [3, 5, 10, 25];

export default function DonatePage() {
  const [copied, setCopied] = useState(false);

  const buyMeCoffeeUrl = `https://buymeacoffee.com/golfboardscores`;

  const handleCopyUsername = async () => {
    try {
      await navigator.clipboard.writeText(`@${VENMO_USERNAME}`);
    } catch {
      const el = document.createElement('textarea');
      el.value = `@${VENMO_USERNAME}`;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Nav */}
      <nav className="bg-white border-b border-amber-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-serif font-bold text-green-900 text-lg">GolfBoard Scores</span>
          </Link>
          <Link to="/login" className="btn-primary text-sm">Sign In</Link>
        </div>
      </nav>

      <main className="flex-1 page-container py-12">
        <div className="max-w-lg mx-auto">

          {/* Hero */}
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">☕</div>
            <h1 className="font-serif font-bold text-3xl text-green-900 mb-3">Support GolfBoard Scores</h1>
            <p className="text-gray-600 leading-relaxed">
              GolfBoard Scores is a passion project built for the love of the game. It's free and always will be.
              If you've gotten value from it, a small tip helps cover server costs and keeps development going.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-800">
            <strong>Please note:</strong> Donations to GolfBoard Scores are entirely voluntary gifts to support an independent hobby project.
            They are <strong>not</strong> required to use any feature of the platform, do not entitle you to any goods, services,
            or special treatment, and are non-refundable.
          </div>

          {/* BuyMeCoffee card */}
          <div className="card p-8 text-center">
            <div className="text-4xl mb-3">☕</div>
            <h2 className="font-serif font-semibold text-green-900 text-xl mb-1">Send via Buy Me a Coffee</h2>
            <p className="text-gray-500 text-sm mb-6">
              Click the link below to open Buy Me a Coffee GolfBoard Scores profile. You can set any amount you'd like to contribute, and it helps support the project!
            </p>

            {/* Suggested amounts — informational only, user sets the amount in Buy Me a Coffee */}
            <div className="mb-6">
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-3">Suggested amounts</p>
              <div className="flex justify-center gap-3">
                {SUGGESTED_AMOUNTS.map(amt => (
                  <span
                    key={amt}
                    className="bg-amber-50 border border-amber-200 text-amber-800 font-semibold rounded-xl px-4 py-2 text-sm"
                  >
                    ${amt}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">You choose the amount inside Buy Me a Coffee — any amount is appreciated!</p>
            </div>

            {/* Open BuyMeCoffee button */}
            <a
              href={buyMeCoffeeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold w-full text-base py-3 flex items-center justify-center gap-2 no-underline"
            >
              <span>☕</span> Open Buy Me a Coffee
            </a>

            <p className="text-xs text-gray-400 mt-3">
              Opens Buy Me a Coffee in your browser or app.
            </p>
          </div>

          {/* What your donation supports */}
          <div className="mt-8">
            <h3 className="font-serif font-semibold text-green-900 text-lg mb-4 text-center">What your support goes toward</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: '🖥️', title: 'Server Costs', desc: 'Database hosting, storage, and API access to keep GolfBoard running.' },
                { icon: '🔧', title: 'Development', desc: 'Time spent building new features and fixing bugs to improve your experience.' },
                { icon: '🆓', title: 'Staying Free', desc: 'Every donation helps keep GolfBoard free and without paywalls for everyone.' },
              ].map(item => (
                <div key={item.title} className="card p-4 text-center">
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <h4 className="font-semibold text-green-900 text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 mt-8">
            Thank you for playing, and for supporting a golfer's passion project. 🏌️
          </p>
        </div>
      </main>

      <footer className="bg-green-900 text-green-300 py-8 px-5 mt-auto">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-serif font-semibold text-amber-200">GolfBoard Scores</span>
          </div>
          <div className="flex gap-5 text-xs">
            <Link to="/" className="hover:text-amber-200">Home</Link>
            <Link to="/terms" className="hover:text-amber-200">Terms</Link>
            <Link to="/privacy" className="hover:text-amber-200">Privacy</Link>
            <Link to="/contact" className="hover:text-amber-200">Contact</Link>
          </div>
          <p className="text-green-500 text-xs">© {new Date().getFullYear()} GolfBoard Scores.</p>
        </div>
      </footer>
    </div>
  );
}